import type { CompoundEntityRef } from '@backstage/catalog-model';
import { createApiRef, type DiscoveryApi, type FetchApi } from '@backstage/core-plugin-api';
import { NotFoundError, ResponseError } from '@backstage/errors';
import type { SyncResult } from '@backstage/plugin-techdocs-react';
import { fetchEventSource } from '@microsoft/fetch-event-source';

export interface TechDocsRdapVersions {
  owner: string;
  repo: string;
  registeredRef: string;
  branches: string[];
  tags: string[];
}

export interface TechDocsRdapClientOptions {
  discoveryApi: DiscoveryApi;
  fetchApi: FetchApi;
}

/**
 * Talks to the techdocs-rdap backend, which extends the stock TechDocs
 * client/storage APIs with awareness of a selected branch/tag: listing what
 * versions exist for an entity, and building/serving docs for whichever one
 * isn't the catalog-registered default.
 */
export class TechDocsRdapClient {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(options: TechDocsRdapClientOptions) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
  }

  private async getApiOrigin(): Promise<string> {
    return this.discoveryApi.getBaseUrl('techdocs-rdap');
  }

  async getVersions(entityId: CompoundEntityRef): Promise<TechDocsRdapVersions> {
    const { kind, namespace, name } = entityId;
    const apiOrigin = await this.getApiOrigin();
    const response = await this.fetchApi.fetch(
      `${apiOrigin}/versions/${namespace}/${kind}/${name}`,
    );
    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }
    return response.json();
  }

  async getEntityDocsForRef(
    entityId: CompoundEntityRef,
    ref: string,
    path: string,
  ): Promise<string> {
    const { kind, namespace, name } = entityId;
    const apiOrigin = await this.getApiOrigin();
    const url = `${apiOrigin}/static/docs-versions/${namespace}/${kind}/${name}/${encodeURIComponent(ref)}/${path}`;
    const response = await this.fetchApi.fetch(
      `${url.endsWith('/') ? url : `${url}/`}index.html`,
    );
    if (response.status === 404) {
      throw new NotFoundError('Page not found.');
    }
    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }
    return response.text();
  }

  /**
   * Downloads the entity's entire TechDocs site as a single PDF or Word file.
   * `ref` is the branch/tag currently selected in the version picker, or
   * undefined for the registered default.
   */
  async exportEntityDocs(
    entityId: CompoundEntityRef,
    ref: string | undefined,
    format: 'pdf' | 'docx',
  ): Promise<{ blob: Blob; filename: string }> {
    const { kind, namespace, name } = entityId;
    const apiOrigin = await this.getApiOrigin();
    const params = new URLSearchParams({ format });
    if (ref) {
      params.set('ref', ref);
    }
    const response = await this.fetchApi.fetch(
      `${apiOrigin}/export/${namespace}/${kind}/${name}?${params}`,
    );
    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }

    const disposition = response.headers.get('content-disposition') ?? '';
    const match = disposition.match(/filename="?([^"]+)"?/i);
    const filename = match?.[1] ?? `${name}.${format}`;

    return { blob: await response.blob(), filename };
  }

  async getBaseUrlForRef(
    oldBaseUrl: string,
    entityId: CompoundEntityRef,
    ref: string,
    path: string,
  ): Promise<string> {
    const { kind, namespace, name } = entityId;
    const apiOrigin = await this.getApiOrigin();
    const newBaseUrl = `${apiOrigin}/static/docs-versions/${namespace}/${kind}/${name}/${encodeURIComponent(ref)}/${path}`;
    return new URL(
      oldBaseUrl,
      newBaseUrl.endsWith('/') ? newBaseUrl : `${newBaseUrl}/`,
    ).toString();
  }

  async syncEntityDocsForRef(
    entityId: CompoundEntityRef,
    ref: string,
    logHandler: (line: string) => void = () => {},
  ): Promise<SyncResult> {
    const { kind, namespace, name } = entityId;
    const apiOrigin = await this.getApiOrigin();
    const url = `${apiOrigin}/sync/${namespace}/${kind}/${name}?ref=${encodeURIComponent(ref)}`;
    return new Promise((resolve, reject) => {
      const ctrl = new AbortController();
      fetchEventSource(url, {
        fetch: this.fetchApi.fetch,
        signal: ctrl.signal,
        onmessage(event) {
          if (event.event === 'log') {
            if (event.data) {
              logHandler(JSON.parse(event.data));
            }
          } else if (event.event === 'finish') {
            let updated = false;
            if (event.data) {
              ({ updated } = JSON.parse(event.data));
            }
            resolve(updated ? 'updated' : 'cached');
          } else if (event.event === 'error') {
            reject(new Error(event.data));
          }
        },
        onerror(err) {
          ctrl.abort();
          reject(err);
          throw err;
        },
      });
    });
  }
}

export const techdocsRdapClientApiRef = createApiRef<TechDocsRdapClient>({
  id: 'rdap.techdocs.client',
});
